import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  ClipboardList,
  SquareX,
  Timer,
  UserCheck,
  ClipboardX,
  Bell,
} from "lucide-react";
import { useHotel } from "@/hooks/useHotel";
import { useScheduleStats } from "@/hooks/useStaffSchedule";
import type { DateRange } from "@/hooks/useDateRange";
import {
  StatsSummaryBlock,
  type StatsSummaryCard,
} from "@/components/common/StatsSummaryBlock";
import { useRequestOperationsStats } from "../hooks/useRequestOperationsStats";

interface RequestStatsProps {
  dateRange: DateRange;
  isKanban: boolean;
}

const formatValue = (
  isLoading: boolean,
  value: React.ReactNode | null | undefined,
): React.ReactNode => {
  if (isLoading) {
    return "…";
  }
  return value ?? "—";
};

const RequestStats: React.FC<RequestStatsProps> = ({ dateRange, isKanban }) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const scheduleStats = useScheduleStats(
    hotelId ?? "",
    isKanban ? todayStr : "",
  );
  const operationsStats = useRequestOperationsStats(dateRange, !isKanban);

  const cards: StatsSummaryCard[] = isKanban
    ? [
        {
          key: "totalTasks",
          label: t("requests.stats.totalTasks"),
          value: formatValue(
            scheduleStats.isLoading,
            scheduleStats.data?.totalTasks,
          ),
          icon: ClipboardList,
          valueClassName: "text-green-600",
        },
        {
          key: "totalTasksCancelled",
          label: t("requests.stats.totalTasksCancelled"),
          value: formatValue(
            scheduleStats.isLoading,
            scheduleStats.data?.totalTasksCancelled,
          ),
          icon: ClipboardX,
          valueClassName: "text-red-600",
        },
        {
          key: "totalPeopleOnSite",
          label: t("requests.stats.totalPeopleOnSite"),
          value: formatValue(
            scheduleStats.isLoading,
            scheduleStats.data?.totalPeopleOnSite,
          ),
          icon: UserCheck,
          valueClassName: "text-eathy-comfort-1",
        },
      ]
    : [
        {
          key: "totalRequests",
          label: t("requests.stats.totalRequests"),
          value: formatValue(
            operationsStats.isLoading,
            operationsStats.totalRequests,
          ),
          icon: Bell,
          valueClassName: "text-green-600",
        },
        {
          key: "canceledRequests",
          label: t("requests.stats.canceledRequests"),
          value: formatValue(
            operationsStats.isLoading,
            operationsStats.canceledRequests,
          ),
          icon: SquareX,
          valueClassName: "text-red-600",
        },
        {
          key: "avgResponseTime",
          label: t("requests.stats.avgResponseTime"),
          value: formatValue(
            operationsStats.isLoading,
            operationsStats.avgResponseTime,
          ),
          icon: Timer,
          valueClassName: "text-eathy-comfort-1",
        },
      ];

  return <StatsSummaryBlock cards={cards} />;
};

export default RequestStats;
